import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { KeyRound, Network, Sparkles } from 'lucide-react';
import {
  Avatar,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  initialsFromName,
} from '@mlm/ui';
import {
  getEarningsBreakdown,
  getGiveMeProgress,
  getLedger,
  getMe,
  getMonthlyPv,
  getRankProgress,
  getSlotStanding,
  getSlotWallet,
  getStorefront,
  getTenantConfig,
  getTotalSaved,
  getUnplacedDownline,
  listMyCodes,
} from '../lib/api';
import { ProductCard } from '../components/product-card';
import { useCartStore } from '../storefront/lib/cart-store';
import { productToAddLine } from '../storefront/lib/cart-map';
import { toDisplay, useBrand } from '../storefront/lib/hooks';
import { useAuthStore } from '../lib/auth-store';
import { useMySlots } from '../lib/hooks/use-my-slots';
import { ActivationLadder } from '../components/activation-ladder';
import { WalletPanel, WalletPanelEmpty } from '../components/wallet/wallet-panel';
import { EarningsBreakdownPanel } from '../components/wallet/earnings-breakdown-panel';
import { StatementList } from '../components/wallet/statement-list';
import { StandingPanel } from '../components/wallet/standing-panel';
import { RankProgressPanel } from '../components/wallet/rank-progress-panel';
import { GiveMePanel } from '../components/wallet/give-me-panel';
import { PvMonthlyPanel } from '../components/wallet/pv-monthly-panel';
import { BinaryPointsHistoryPanel } from '../components/wallet/binary-points-history-panel';
import { PendingOrdersPanel } from '../components/dashboard/pending-orders-panel';
import { AnnouncementsPanel } from '../components/dashboard/announcements-panel';
import { TotalSavedPanel } from '../components/dashboard/total-saved-panel';

/**
 * Member home, a passbook. The onboarding tutorial (ActivationLadder) leads and
 * is the centerpiece for a new member. Once a slot is active, the body is a
 * wallet+standing rail beside the statement, with contextual Create/Place slot
 * actions when there's actually something to act on. Navigation stays in the
 * sidebar — these are actions, not a second nav.
 */
export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const {
    slots,
    hasAnyActiveSlot,
    activeSlotLabel,
    activeSlotId,
    isLoading: slotsLoading,
  } = useMySlots();
  const slotReady = !slotsLoading && hasAnyActiveSlot;
  const tenantQ = useQuery({ queryKey: ['tenant-config'], queryFn: getTenantConfig });

  const walletQ = useQuery({
    queryKey: ['wallet', activeSlotId],
    queryFn: () => getSlotWallet(activeSlotId),
    enabled: slotReady,
  });
  const ledgerQ = useQuery({
    queryKey: ['ledger', activeSlotId, 1, 8],
    queryFn: () => getLedger({ slotId: activeSlotId ?? undefined, pageSize: 8 }),
    enabled: slotReady,
  });
  const standingQ = useQuery({
    queryKey: ['standing', activeSlotId],
    queryFn: () => getSlotStanding(activeSlotId!),
    enabled: slotReady && !!activeSlotId,
  });
  const earningsBreakdownQ = useQuery({
    queryKey: ['earnings-breakdown', activeSlotId],
    queryFn: () => getEarningsBreakdown(activeSlotId!),
    enabled: slotReady && !!activeSlotId,
  });
  const rankProgressQ = useQuery({
    queryKey: ['rank-progress', activeSlotId],
    queryFn: () => getRankProgress(activeSlotId!),
    enabled: slotReady && !!activeSlotId,
  });
  const giveMeQ = useQuery({
    queryKey: ['give-me-progress', activeSlotId],
    queryFn: () => getGiveMeProgress(activeSlotId!),
    enabled: slotReady && !!activeSlotId,
  });
  const meQ = useQuery({ queryKey: ['me'], queryFn: getMe });
  // Placement only exists to feed the binary tree, so with the plan off there's
  // nothing to place — and the genealogy page hides its Binary tab, which is
  // where the "Place slot" button leads.
  const binaryOn =
    !!meQ.data && !(meQ.data.tenantPolicy?.disabledPlanCodes ?? []).includes('binary');
  const unplacedQ = useQuery({
    queryKey: ['unplaced-downline', activeSlotId],
    queryFn: () => getUnplacedDownline(activeSlotId!),
    enabled: slotReady && !!activeSlotId && binaryOn,
  });
  const memberPvDisplay = meQ.data?.tenantPolicy?.memberPvDisplay ?? 'off';
  const pvMonthlyQ = useQuery({
    queryKey: ['pv-monthly', activeSlotId],
    queryFn: () => getMonthlyPv(activeSlotId!),
    enabled: slotReady && !!activeSlotId && memberPvDisplay !== 'off',
  });
  const unredeemedQ = useQuery({
    queryKey: ['my-codes-available'],
    queryFn: () => listMyCodes({ onlyAvailable: true }),
  });
  // Member-level (all slots) — keyed on nothing slot-specific. Fires only when
  // the tenant flag says the card exists; the endpoint re-gates server-side.
  const totalSavedQ = useQuery({
    queryKey: ['total-saved'],
    queryFn: getTotalSaved,
    enabled: tenantQ.data?.totalSavedCounter === true,
  });

  if (slotsLoading || !user) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-28" />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Skeleton className="h-56 lg:col-span-1" />
          <Skeleton className="h-56 lg:col-span-2" />
        </div>
      </div>
    );
  }

  const disabledPlanCodes = meQ.data?.tenantPolicy?.disabledPlanCodes;
  const unredeemedCount = unredeemedQ.data?.length ?? 0;
  const unplacedCount = unplacedQ.data?.length ?? 0;
  // Slot count toward the tenant limit excludes cancelled slots (they free a
  // slot), mirroring the createSlot enforcement.
  const slotCount = slots.filter((s) => s.status !== 'cancelled').length;
  const slotLimit = tenantQ.data?.maxSlotsPerMember ?? null;

  const greetingName = user.firstName ?? user.fullName ?? user.email;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Avatar
          size="lg"
          className="h-12 w-12"
          src={user.profilePictureUrl ?? undefined}
          initials={initialsFromName(user.fullName ?? user.email, 'ME')}
        />
        <div className="min-w-0">
          <div className="truncate text-lg font-semibold">Hi, {greetingName}</div>
          <div className="text-sm text-muted-foreground">Welcome back to your dashboard.</div>
        </div>
      </div>

      <ActivationLadder />

      <AnnouncementsPanel />

      {hasAnyActiveSlot ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="inline-flex items-center gap-1.5 rounded-full border bg-muted/40 px-3 py-1 text-sm"
              title="Slots you own toward your tenant slot limit"
            >
              <Network className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-muted-foreground">Slots</span>
              <span className="font-semibold tabular-nums">
                {slotCount}
                {slotLimit != null ? ` / ${slotLimit}` : ''}
              </span>
            </span>
            <SlotActions unredeemedCount={unredeemedCount} unplacedCount={unplacedCount} />
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="space-y-4 lg:col-span-1">
              {walletQ.data ? (
                <WalletPanel
                  wallet={walletQ.data}
                  slotLabel={activeSlotLabel}
                  topupPayoffHref={
                    meQ.data?.tenantPolicy?.walletTopupEnabled &&
                    meQ.data?.tenantPolicy?.cdTopupSettlesDebt
                      ? '/wallet-topup'
                      : null
                  }
                />
              ) : (
                <Card>
                  <CardContent className="p-6">
                    <Skeleton className="h-40 w-full" />
                  </CardContent>
                </Card>
              )}
              {tenantQ.data?.totalSavedCounter &&
              totalSavedQ.data?.enabled &&
              totalSavedQ.data.totals ? (
                <TotalSavedPanel totals={totalSavedQ.data.totals} />
              ) : null}
              {standingQ.data ? (
                <StandingPanel
                  standing={standingQ.data}
                  disabledPlanCodes={disabledPlanCodes}
                  showSponsor={tenantQ.data?.showSponsorOnDashboard}
                />
              ) : null}
              {earningsBreakdownQ.data ? (
                <EarningsBreakdownPanel
                  entries={earningsBreakdownQ.data}
                  slotId={activeSlotId}
                  kindLabels={meQ.data?.ledgerLabels}
                  planLabels={meQ.data?.ledgerSourcePlanLabels}
                  legacyOffset={walletQ.data?.legacyEarnedOffset}
                />
              ) : null}
              {(standingQ.data?.binaryVariant === 'legacy' ||
                standingQ.data?.binaryVariant === 'extreme') &&
              activeSlotId ? (
                <BinaryPointsHistoryPanel slotId={activeSlotId} />
              ) : null}
              {memberPvDisplay !== 'off' && pvMonthlyQ.data ? (
                <PvMonthlyPanel
                  buckets={pvMonthlyQ.data.buckets}
                  showGpv={memberPvDisplay === 'ppv_and_gpv'}
                />
              ) : null}
              {rankProgressQ.data?.stairstepActive && rankProgressQ.data.progress ? (
                <RankProgressPanel progress={rankProgressQ.data.progress} />
              ) : null}
              {giveMeQ.data?.planActive && giveMeQ.data.rows.length > 0 ? (
                <GiveMePanel rows={giveMeQ.data.rows} title={giveMeQ.data.planLabel} />
              ) : null}
              <PendingOrdersPanel slotId={activeSlotId} />
            </div>
            <div className="lg:col-span-2">
              {ledgerQ.isLoading ? (
                <Card>
                  <CardContent className="p-6">
                    <Skeleton className="h-48 w-full" />
                  </CardContent>
                </Card>
              ) : (
                <StatementList
                  entries={ledgerQ.data ?? []}
                  kindLabels={meQ.data?.ledgerLabels}
                  planLabels={meQ.data?.ledgerSourcePlanLabels}
                  subtitle={activeSlotLabel ?? undefined}
                  viewAllHref="/earnings"
                />
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          <NoSlotKitsSection />
          <WalletPanelEmpty />
        </>
      )}
    </div>
  );
}

/**
 * Pre-slot members need a path to their first kit. A tight grid of `isKit=true`
 * storefront products; Buy Now drops the kit into the storefront cart and goes
 * straight to `/checkout` — the same checkout everything else uses now that the
 * in-app store is gone. Auto-hides when the storefront returns no kits (admin
 * hasn't set any up yet).
 */
function NoSlotKitsSection() {
  const navigate = useNavigate();
  // The STOREFRONT cart — the in-app store this used to feed is gone, so a kit
  // is bought through the same checkout as everything else. Kits can never have
  // variations (`kit_cannot_have_variants`), so a kit line needs no variant id.
  const lines = useCartStore((s) => s.lines);
  const addToCart = useCartStore((s) => s.add);
  const brand = useBrand();
  // No slotId ON PURPOSE — do not "fix" this to pass the active slot. This
  // section only renders in the `hasAnyActiveSlot === false` branch above, so
  // there is nothing to price against and the server falls back to base price.
  // The bare `['storefront']` key is likewise deliberate: it must NOT share a
  // cache entry with the slot-keyed `['storefront', slotId]` used everywhere else.
  const storefrontQ = useQuery({ queryKey: ['storefront'], queryFn: () => getStorefront() });
  const meQ = useQuery({ queryKey: ['me'], queryFn: getMe });
  const linkKitIds = meQ.data?.registeredLinkKitIds ?? null;
  const allKits = (storefrontQ.data?.products ?? []).filter((p) => p.isKit);
  // Normalized rows for the cart mapper, keyed by id so the card's own raw row
  // still drives the display.
  const displayById = useMemo(
    () => new Map(allKits.map((p) => [p.id, toDisplay(p)])),
    [allKits],
  );
  const inCartQty = (productId: string) =>
    Object.values(lines).find((l) => l.productId === productId)?.qty ?? 0;
  const addKit = (productId: string) => {
    const d = displayById.get(productId);
    if (d) addToCart(productToAddLine(d, brand, { qty: 1 }));
  };
  // Replicated-link packages: scope the lead to the activation kits tagged to
  // their sponsor tier's membership. null = no restriction (show all).
  const kits = linkKitIds ? allKits.filter((p) => linkKitIds.includes(p.id)) : allKits;

  if (storefrontQ.isLoading || meQ.isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4 text-primary" /> Pick your activation kit
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-56" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }
  if (kits.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Sparkles className="h-4 w-4 text-primary" /> Pick your activation kit
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="mb-3 text-sm text-muted-foreground">
          Buying a kit creates a Code + PIN you redeem to activate your first slot.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {kits.map((p) => (
            <ProductCard
              key={p.id}
              view={{
                id: p.id,
                name: p.name,
                sku: p.sku,
                description: p.description,
                images: p.images,
                isKit: p.isKit,
                featured: p.featured,
                pv: p.pv,
                price: p.priceEffective,
                priceBefore: p.priceBase,
                discountPct: Number(p.appliedDiscountPct),
              }}
              basePath="/product"
              inCart={inCartQty(p.id)}
              onAdd={() => addKit(p.id)}
              onBuyNow={() => {
                addKit(p.id);
                navigate('/checkout');
              }}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Contextual actions, not navigation: shown only when there's something to do.
 * Each links to the proven flow (Vault redeem / genealogy seat-picker).
 */
function SlotActions({
  unredeemedCount,
  unplacedCount,
}: {
  unredeemedCount: number;
  unplacedCount: number;
}) {
  if (unredeemedCount === 0 && unplacedCount === 0) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {unredeemedCount > 0 ? (
        <Button asChild>
          <Link to="/code-vault">
            <KeyRound className="h-4 w-4" /> Create slot
            <span className="ml-1 rounded bg-primary-foreground/20 px-1.5 text-xs tabular-nums">
              {unredeemedCount}
            </span>
          </Link>
        </Button>
      ) : null}
      {unplacedCount > 0 ? (
        <Button asChild variant="outline">
          <Link to="/genealogy">
            <Network className="h-4 w-4" /> Place slot
            <span className="ml-1 rounded bg-muted px-1.5 text-xs tabular-nums">
              {unplacedCount}
            </span>
          </Link>
        </Button>
      ) : null}
    </div>
  );
}
